// DOM.com.cy search scanner — fetches the server-rendered catalog page
// through the proxy chain and parses the listing cards.
//
// The old public GraphQL API (api.staging.app.dom.com.cy) was shut down when
// DOM moved to 1C-Bitrix; the catalog URL scheme (/en/catalog/sale/city-.../)
// is unchanged and now renders the results directly in HTML.

import { sanitizeText } from './buysellService';
import { extractDomComId, fetchDomComHtml, parseDomPrice } from './domcomService';

const DOM_BASE_URL = 'https://dom.com.cy';

/**
 * Make sure the search URL is the English catalog page with a trailing slash
 * (Bitrix 301-redirects otherwise; some CORS proxies don't follow redirects).
 */
const normalizeSearchUrl = (searchUrl) => {
  const url = new URL(searchUrl);
  let path = url.pathname;
  if (!/^\/(en|ru)\//i.test(path)) path = `/en${path}`;
  if (!path.endsWith('/')) path += '/';
  url.pathname = path;
  return url.toString();
};

/**
 * Extract title from a listing card.
 * @param {Element} card
 * @param {Element} link
 */
const extractTitle = (card, link) => {
  const nameEl = card.querySelector('.info-block .name, [itemprop="name"]');
  const name = nameEl?.textContent?.trim();
  if (name) return name;

  const img = link.querySelector('img') || card.querySelector('img[alt]');
  const alt = img?.getAttribute('alt')?.trim();
  if (alt && !/^photo\s*\d*$/i.test(alt)) return alt;

  return link.textContent?.trim() || '';
};

/**
 * Extract price from a listing card (schema.org meta first, visible text second).
 * @param {Element} card
 */
const extractPrice = (card) => {
  const meta = card.querySelector('[itemprop="offers"] [itemprop="price"], [itemprop="price"]');
  if (meta) {
    const p = parseDomPrice(meta.getAttribute('content') || meta.textContent);
    if (p) return p;
  }
  const visible = card.querySelector('.price-block .price-main .price');
  if (visible) {
    const clone = visible.cloneNode(true);
    clone.querySelectorAll('.currency, .vat, sup').forEach((el) => el.remove());
    return parseDomPrice(clone.textContent);
  }
  return 0;
};

/**
 * Scan a DOM.com.cy search URL and return normalized results.
 *
 * @param {string} searchUrl - DOM.com.cy search page URL
 *   e.g. https://dom.com.cy/en/catalog/sale/city-larnaca/area-aradippou-1/type-house/bedrooms-3-4-5/year-to-2025
 * @returns {Promise<Array<{id: string, title: string, price: number|string, url: string}>>}
 */
export const scanDomComSearch = async (searchUrl) => {
  const fetchUrl = normalizeSearchUrl(searchUrl);
  console.log(`[DOM.com.cy Search] Fetching: ${fetchUrl}`);

  const html = await fetchDomComHtml(fetchUrl);

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  doc.querySelectorAll('script, style, noscript, iframe').forEach((el) => el.remove());

  const cards = doc.querySelectorAll('.catalog-content .catalog-item, .catalog-item');
  const seen = new Set();
  const items = [];

  for (const card of cards) {
    const link = card.querySelector('a[href*="/catalog/sale/"], a[href*="/catalog/rent/"]');
    if (!link) continue;

    const href = link.getAttribute('href') || '';
    const fullUrl = href.startsWith('http') ? href : `${DOM_BASE_URL}${href}`;
    const id = extractDomComId(fullUrl);
    if (!id || seen.has(id)) continue;
    seen.add(id);

    const publication = /\/catalog\/rent\//i.test(fullUrl) ? 'rent' : 'sale';
    const url = `${DOM_BASE_URL}/en/catalog/${publication}/${id}/`;

    items.push({
      id,
      title: sanitizeText(extractTitle(card, link), 200),
      price: extractPrice(card),
      url,
    });
  }

  // Fallback: if the card selector ever changes, at least surface the links.
  if (items.length === 0) {
    for (const link of doc.querySelectorAll('a[href*="/catalog/sale/"], a[href*="/catalog/rent/"]')) {
      const href = link.getAttribute('href') || '';
      const fullUrl = href.startsWith('http') ? href : `${DOM_BASE_URL}${href}`;
      const id = extractDomComId(fullUrl);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const publication = /\/catalog\/rent\//i.test(fullUrl) ? 'rent' : 'sale';
      items.push({
        id,
        title: sanitizeText(link.getAttribute('title') || link.textContent?.trim() || '', 200),
        price: 0,
        url: `${DOM_BASE_URL}/en/catalog/${publication}/${id}/`,
      });
    }
  }

  console.log(
    `[DOM.com.cy Search] Found ${items.length} properties` +
    ` (${items.filter((i) => i.title).length} with titles, ` +
    `${items.filter((i) => i.price > 0).length} with prices)`
  );

  return items;
};
